import React from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Container,
  Paper,
  useTheme,
  Alert,
} from "@mui/material";
import { NavigateNext, People } from "@mui/icons-material";
import { getUserType } from "../../utils/AuthHelper";

export default function UserManagementLayout({ children }) {
  const theme = useTheme();
  const location = useLocation();
  const userType = getUserType();

  // Check if user has access to user management
  const canAccessUserManagement =
    userType === "admin" || userType === "superuser";

  if (!canAccessUserManagement) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          You don't have permission to access User Management. Only System
          Administrators and Organization Administrators can access this module.
        </Alert>
      </Container>
    );
  }

  // Get current page title from URL
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes("all-users")) return "All Users";
    if (path.includes("add-user")) return "Add New User";
    if (path.includes("species")) return "Species Management";
    return "User Management";
  };

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
      {/* Breadcrumbs */}
      <Box sx={{ mb: 3, mt: 2 }}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
          <Link
            href="/dashboard"
            color="inherit"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Dashboard
          </Link>
          <Link
            href="#"
            color="inherit"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            <People sx={{ mr: 0.5 }} fontSize="small" />
            User Management
          </Link>
          <Typography
            color="text.primary"
            sx={{ display: "flex", alignItems: "center" }}
          >
            {getPageTitle()}
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 2 }}
        >
          <People fontSize="large" />
          {getPageTitle()}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {getPageTitle() === "All Users" &&
            "Manage user accounts and permissions"}
          {getPageTitle() === "Add New User" && "Create new user accounts"}
          {getPageTitle() === "Species Management" &&
            "Manage species and permissions"}
        </Typography>
      </Box>

      {/* Content Area - Fixed overflow issue */}
      <Paper
        sx={{
          borderRadius: 3,
          overflow: "visible", // Changed from "hidden" to "visible"
          minHeight: "calc(100vh - 250px)",
          backgroundColor:
            theme.palette.mode === "dark" ? "background.default" : "#fff",
          p: { xs: 1, sm: 2, md: 3 }, // Added padding
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Render children passed from App.js */}
        <Box sx={{ flex: 1, overflow: "visible" }}>{children}</Box>
      </Paper>
    </Container>
  );
}
